(() => {
  'use strict';
  if (customElements.get('me-product-delivery')) return;
  class MEDelivery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.lifecycle = new AbortController();
      const listenerOptions = {signal: this.lifecycle.signal};
      this.form = this.querySelector('form');
      this.country = this.form.elements['delivery-country'];
      this.zip = this.form.elements['delivery-zip'];
      this.checkButton = this.querySelector('.me-delivery__check');
      this.savedButton = this.querySelector('[data-use-saved]');
      this.status = this.querySelector('[role="status"]');
      this.results = this.querySelector('.me-delivery__results');
      this.note = this.querySelector('.me-delivery__note');
      this.teaser = this.querySelector('.me-delivery__teaser');
      this.productInfo = this.closest('product-info');
      this.version = (this.version || 0) + 1;
      this.setBusy(false);
      const savedNode = this.querySelector('[data-saved-address]');
      if (savedNode) {
        this.savedAddress = {...savedNode.dataset};
        savedNode.remove();
      }
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.run(false); }, listenerOptions);
      this.savedButton?.addEventListener('click', () => this.run(true), listenerOptions);
      this.form.addEventListener('input', () => this.invalidate(), listenerOptions);
      this.country.addEventListener('change', () => { this.invalidate(); this.zip.inputMode = this.country.value === 'US' ? 'numeric' : 'text'; }, listenerOptions);
      this.zip.inputMode = this.country.value === 'US' ? 'numeric' : 'text';
      this.querySelector('[data-delivery-login]')?.addEventListener('click', (event) => {
        const link = event.currentTarget;
        const loginURL = new URL(link.href, window.location.origin);
        if (loginURL.origin === window.location.origin && loginURL.searchParams.has('return_to')) {
          loginURL.searchParams.set('return_to', window.location.pathname + window.location.search + '#' + this.id);
          link.href = loginURL.href;
        }
      }, listenerOptions);
      this.onProductChange = (event) => {
        if (this.contains(event.target)) return;
        if (event.target.matches('[name="id"], [name="quantity"]') || event.target.closest('variant-selects')) {
          this.invalidate('Selection changed. Check delivery again for the current option and quantity.');
        }
      };
      this.productInfo?.addEventListener('change', this.onProductChange, listenerOptions);
      this.productInfo?.addEventListener('input', this.onProductChange, listenerOptions);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          if (this.quoteSelection && this.selectionKey(this.selection()) !== this.quoteSelection) {
            this.invalidate('Quantity changed. Check delivery again for the current quantity.');
          }
        });
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.version += 1;
      this.lifecycle?.abort();
      this.unsubscribeQuantity?.();
      this.initialized = false;
    }
    invalidate(message = '') {
      const hadEstimate = this.hasRequested;
      this.version += 1;
      this.controller?.abort();
      this.controller = null;
      this.setBusy(false);
      this.results.replaceChildren();
      this.results.hidden = true;
      this.note.hidden = true;
      this.status.textContent = hadEstimate ? message : '';
      this.zip.removeAttribute('aria-invalid');
      this.quoteSelection = null;
      this.teaser.textContent = 'Check cost & estimated arrival';
    }
    setBusy(busy) {
      this.checkButton.disabled = busy;
      if (this.savedButton) this.savedButton.disabled = busy;
      this.checkButton.textContent = busy ? 'Checking delivery…' : 'Check delivery';
      this.results.setAttribute('aria-busy', String(busy));
    }
    selection() {
      const picker = this.productInfo?.querySelector('variant-selects');
      const source = picker ? picker.querySelector('[data-selected-variant]') : this.querySelector('[data-delivery-variant]');
      let variant;
      try { variant = JSON.parse(source?.textContent || 'null'); } catch { return null; }
      const form = this.productInfo?.querySelector(`form[id="product-form-${this.dataset.section}"]`);
      const id = form?.querySelector('[name="id"]')?.value;
      const qty = this.productInfo?.querySelector('[name="quantity"]');
      if (!variant || !id || String(variant.id) !== id || !variant.available) return null;
      if (variant.requires_shipping === false) return {shipping: false};
      const button = form?.querySelector('[type="submit"]');
      if (button?.disabled) return null;
      const quantity = qty ? Number(qty.value) : 1;
      if (!Number.isSafeInteger(quantity) || quantity < 1 || (qty && !qty.checkValidity())) return null;
      if (form?.querySelector('[name="selling_plan"]')?.value) return null;
      return {variantId: String(variant.id), quantity, title: variant.public_title || variant.title, shipping: true};
    }
    selectionKey(selection) {
      return selection ? `${selection.variantId}:${selection.quantity}:${selection.shipping}` : '';
    }
    async run(useSaved) {
      this.invalidate();
      this.hasRequested = true;
      const selection = this.selection();
      if (!selection) { this.status.textContent = 'Choose an available option and valid quantity, then check delivery.'; return; }
      if (!selection.shipping) { this.status.textContent = 'This item does not require shipping.'; return; }
      const source = useSaved ? this.savedAddress : {countryCode: this.country.value, zip: this.zip.value.trim().toUpperCase()};
      if (!source) return;
      const countryCode = source.countryCode;
      const address = {zip: source.zip, provinceCode: source.provinceCode, city: source.city, address1: source.address1, address2: source.address2};
      if (!useSaved && ((countryCode === 'US' && !/^\d{5}(?:-\d{4})?$/.test(address.zip)) || (countryCode === 'CA' && !/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/.test(address.zip)))) {
        this.zip.setAttribute('aria-invalid', 'true');
        this.status.textContent = countryCode === 'US' ? 'Enter a valid 5-digit ZIP code (or ZIP+4).' : 'Enter a valid ZIP / postal code for your destination.';
        this.zip.focus();
        return;
      }
      const version = this.version;
      this.quoteSelection = this.selectionKey(selection);
      const controller = new AbortController();
      this.controller = controller;
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 30000);
      this.setBusy(true);
      this.status.textContent = 'Checking current shipping options…';
      try {
        if (!window.MEDeliveryAPI) throw new Error('Estimator unavailable');
        const response = await window.MEDeliveryAPI.quote({...selection, countryCode, address, signal: controller.signal});
        if (version !== this.version || !this.isConnected) return;
        if (this.selectionKey(this.selection()) !== this.quoteSelection) {
          this.invalidate('Selection changed. Check delivery again for the current option and quantity.');
          return;
        }
        if (!response.groups.length || response.groups.some(group => !group.options.length)) {
          this.status.textContent = 'No shipping estimate was returned for this destination. Try a saved address, check at checkout, or ask us about delivery.';
          return;
        }
        this.render(response.groups);
        const destination = [address.zip, countryCode].filter(Boolean).join(', ');
        const option = selection.title && selection.title !== 'Default Title' ? ` · ${selection.title}` : '';
        this.status.textContent = `Estimated shipping to ${destination} · quantity ${selection.quantity}${option}${useSaved ? ' · saved address' : ''}`;
        this.teaser.textContent = `Estimate ready for ${destination}`;
        this.results.hidden = false;
        this.note.hidden = false;
      } catch (error) {
        if (version !== this.version || !this.isConnected) return;
        if (error.name === 'AbortError' && !timedOut) return;
        if (error.code === 'INVALID_INPUT') {
          this.status.textContent = error.message;
          this.zip.setAttribute('aria-invalid', 'true');
          return;
        }
        if (error.code === 'CART_CHANGED') {
          this.status.textContent = 'This option or quantity is unavailable for an estimate. Check your selection or contact us.';
          return;
        }
        if (error.code === 'RATE_UNAVAILABLE') {
          this.status.textContent = 'No shipping estimate was returned for this destination. Try a saved address, check at checkout, or ask us about delivery.';
          return;
        }
        this.status.textContent = timedOut ? 'Shipping services are taking longer than expected. Please try again or check at checkout.' : 'We couldn’t get a shipping estimate. Check your destination, try a saved address, or see shipping options at checkout.';
      } finally {
        clearTimeout(timeout);
        if (version === this.version) { this.setBusy(false); this.controller = null; }
      }
    }
    render(groups) {
      this.results.replaceChildren();
      groups.forEach((group, index) => {
        if (groups.length > 1) {
          const heading = document.createElement('h3');
          heading.className = 'me-delivery__group';
          heading.textContent = `Shipment ${index + 1} — shipping charged separately`;
          this.results.append(heading);
        }
        const list = document.createElement('ul');
        list.className = 'me-delivery__rates';
        group.options.forEach(option => {
          const item = document.createElement('li'); item.className = 'me-delivery__rate';
          const head = document.createElement('div'); head.className = 'me-delivery__rate-head';
          const name = document.createElement('strong'); name.textContent = option.title || 'Shipping';
          const cost = document.createElement('span'); cost.className = 'me-delivery__rate-cost';
          cost.textContent = Number(option.estimatedCost.amount) === 0 ? 'Free' : new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:option.estimatedCost.currencyCode, currencyDisplay:'code'}).format(Number(option.estimatedCost.amount));
          head.append(name, cost);
          const timing = document.createElement('div'); timing.className = 'me-delivery__rate-time';
          timing.textContent = option.description || 'Check final shipping details at checkout.';
          item.append(head, timing); list.append(item);
        });
        this.results.append(list);
      });
    }
  }
  customElements.define('me-product-delivery', MEDelivery);
})();
